(() => {
  const settings = {
    collectionHandle: 'fabric-samples',
    maxItems: 10,
    messages: {
      duplicate: 'This fabric sample is already in your cart.',
      max: 'You can order a maximum of 10 fabric samples.',
      generic: 'Fabric samples could not be added. Please try again.',
    },
  };

  const normalizeId = (value) => (value == null ? '' : String(value));
  const isTruthy = (value) => value === true || ['true', '1', 'yes'].includes(String(value).toLowerCase());

  const initialSampleProductIds = () =>
    (window.fabricSampleCart?.productIds || []).map(normalizeId).filter(Boolean);

  const domSampleLines = () => Array.from(document.querySelectorAll('[data-fabric-sample-line="true"]'));

  const getDomSampleIds = () => {
    const productIds = new Set();
    const variantIds = new Set();

    domSampleLines().forEach((line) => {
      const productId = normalizeId(line.dataset.fabricSampleProductId);
      const variantId = normalizeId(line.dataset.fabricSampleVariantId);

      if (productId) productIds.add(productId);
      if (variantId) variantIds.add(variantId);
    });

    return { productIds, variantIds };
  };

  const isSampleCartItem = (item, sampleProductIds, domIds) => {
    const productId = normalizeId(item.product_id);
    const variantId = normalizeId(item.variant_id);
    const properties = item.properties || {};

    return (
      isTruthy(properties._fabric_sample) ||
      sampleProductIds.has(productId) ||
      domIds.productIds.has(productId) ||
      domIds.variantIds.has(variantId)
    );
  };

  const getCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('Unable to load cart');
      return response.json();
    });

  const getSampleSummary = (cart) => {
    const cartProductIds = new Set(cart.items.map((item) => normalizeId(item.product_id)).filter(Boolean));
    const sampleProductIds = new Set(initialSampleProductIds().filter((id) => cartProductIds.has(id)));
    const domIds = getDomSampleIds();
    const keys = new Set();
    const productIds = new Set();
    const variantIds = new Set();

    cart.items.forEach((item) => {
      if (!isSampleCartItem(item, sampleProductIds, domIds)) return;

      const productId = normalizeId(item.product_id);
      const variantId = normalizeId(item.variant_id);

      if (productId) {
        productIds.add(productId);
        keys.add(`product:${productId}`);
      } else if (variantId) {
        keys.add(`variant:${variantId}`);
      }

      if (variantId) variantIds.add(variantId);
    });

    return {
      count: keys.size,
      productIds,
      variantIds,
    };
  };

  const normalizeIncomingItems = (items) => {
    const uniqueItems = [];
    const seen = new Set();

    items.forEach((item) => {
      const variantId = normalizeId(item.id || item.variantId || item.variant_id);
      const productId = normalizeId(item.productId || item.product_id);
      const key = productId ? `product:${productId}` : `variant:${variantId}`;

      if (!variantId || seen.has(key)) return;

      seen.add(key);
      uniqueItems.push({
        id: variantId,
        productId,
        key,
        properties: {
          ...(item.properties || {}),
          _fabric_sample: 'true',
        },
      });
    });

    return uniqueItems;
  };

  const toCartPayload = (item) => ({
    id: item.id,
    quantity: 1,
    properties: item.properties,
  });

  async function prepareItemsForAdd(items) {
    const incomingItems = normalizeIncomingItems(items);

    if (!incomingItems.length) {
      return { allowed: false, items: [], message: settings.messages.generic };
    }

    try {
      const cart = await getCart();
      const summary = getSampleSummary(cart);
      const addableItems = incomingItems.filter((item) => {
        if (item.productId && summary.productIds.has(item.productId)) return false;
        return !summary.variantIds.has(item.id);
      });
      const remainingSlots = settings.maxItems - summary.count;

      if (!addableItems.length) {
        return { allowed: false, items: [], message: settings.messages.duplicate };
      }

      if (remainingSlots <= 0 || addableItems.length > remainingSlots) {
        return { allowed: false, items: [], message: settings.messages.max };
      }

      return {
        allowed: true,
        items: addableItems.map(toCartPayload),
        skippedCount: incomingItems.length - addableItems.length,
      };
    } catch (error) {
      console.error(error);
      return { allowed: false, items: [], message: settings.messages.generic };
    }
  }

  function syncQuantityInputs(root = document) {
    root.querySelectorAll('input[data-fabric-sample-quantity="true"]').forEach((input) => {
      input.max = '1';
      input.dataset.max = '1';

      if (parseInt(input.value, 10) > 1 || !input.value) {
        input.value = '1';
      }

      const quantityInput = input.closest('quantity-input');
      const plusButton = quantityInput?.querySelector('.quantity__button[name="plus"]');

      if (plusButton) {
        plusButton.disabled = parseInt(input.value, 10) >= 1;
        plusButton.classList.toggle('disabled', parseInt(input.value, 10) >= 1);
      }
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const plusButton = event.target.closest('.quantity__button[name="plus"]');
      if (!plusButton) return;

      const input = plusButton.closest('quantity-input')?.querySelector('input[data-fabric-sample-quantity="true"]');
      if (!input) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      input.value = '1';
      syncQuantityInputs(plusButton.closest('quantity-input') || document);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest('input[data-fabric-sample-quantity="true"]');
      if (!input) return;

      if (parseInt(input.value, 10) > 1 || !input.value) {
        input.value = '1';
      }

      syncQuantityInputs(input.closest('quantity-input') || document);
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => syncQuantityInputs());

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(() => syncQuantityInputs());
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.fabricSampleCartRules = {
    settings,
    prepareItemsForAdd,
    syncQuantityInputs,
  };
})();

document.addEventListener("DOMContentLoaded", function () {
  const countEl = document.querySelector(".wishlist-header-icon-count");
  let wishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
  if (wishlist.length && typeof wishlist[0] === "string") {
    localStorage.removeItem("wishlist");
    wishlist = [];
  }
  function updateCount() {
    if (countEl) {
      countEl.textContent = wishlist.length;
    }
  }

  function updateAllButtons() {
    document.querySelectorAll(".wishlist-icon-text").forEach((button) => {
      const variantId = button.getAttribute("data-variant-id");
      if (!variantId) return;
      const exists = wishlist.some(item => item.variantId == variantId);
      button.classList.toggle("active", exists);
    });
  }

  updateCount();
  updateAllButtons();

  document.addEventListener("click", function (e) {

    const button = e.target.closest(".wishlist-icon-text");
    if (!button) return;

    e.preventDefault();
    e.stopPropagation();

    const variantId = button.getAttribute("data-variant-id");
    const handle = button.getAttribute("product-handle");

    if (!variantId || !handle) return;
    const index = wishlist.findIndex(item => item.variantId == variantId);
    if (index > -1) {
      wishlist.splice(index, 1);
    } else {
      wishlist.push({
        variantId: variantId,
        handle: handle
      });
    }

    localStorage.setItem("wishlist", JSON.stringify(wishlist));
    updateAllButtons();
    updateCount();
  });

  let scrollTimeout;
  window.addEventListener("scroll", function () {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      updateAllButtons();
    }, 120);
  });

  const observer = new MutationObserver(() => {
    updateAllButtons();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});

window.initProductCardHovers = function() {
  const items = document.querySelectorAll('.fabric-item');

  items.forEach(function(item) {
    if (item.dataset.initialized) return; 
    item.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const url = this.getAttribute('data-url');
      if (url) window.location.href = url;
    });
    item.dataset.initialized = true;
  });

  document.querySelectorAll('.card-product-variant-container').forEach(function(container) {
    const card = container.closest('.card.card--standard');
    if (!card || container.dataset.initialized) return;

    const mediaImg = card.querySelector('.card__media img');
    if (!mediaImg) return;

    const originalSrc = mediaImg.getAttribute('src');
    const originalSrcset = mediaImg.getAttribute('srcset');
    let timeout;

    container.querySelectorAll('.fabric-item').forEach(function(item) {
      item.addEventListener('mouseenter', function() {
        clearTimeout(timeout);
        const newImg = this.getAttribute('data-variant-image');
        if (newImg) {
          mediaImg.setAttribute('src', newImg);
          mediaImg.setAttribute('srcset', newImg);
        }
      });

      item.addEventListener('mouseleave', function() {
        timeout = setTimeout(function() {
          mediaImg.setAttribute('src', originalSrc);
          if (originalSrcset) mediaImg.setAttribute('srcset', originalSrcset);
        }, 1200);
      });
    });
    container.dataset.initialized = true;
  });
};

document.addEventListener('DOMContentLoaded', () => {
  window.initProductCardHovers();
});

document.addEventListener('DOMContentLoaded', function() {
  
  const grid = document.querySelector('body.collection #product-grid');
  if (!grid) return;

  const urlParams = new URLSearchParams(window.location.search);
  const hasFilters = Array.from(urlParams.keys()).some(key => key.startsWith('filter.'));
  const hasSorting = urlParams.has('sort_by');
  if (hasFilters || hasSorting) {
      console.log('Shuffle disabled: Filters or Sorting detected.');
      return;
  }

  const products = Array.from(grid.children);

  for (let i = products.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [products[i], products[j]] = [products[j], products[i]];
  }

  grid.innerHTML = '';
  products.forEach(product => grid.appendChild(product));
});

$(document).ready(function () {
  const checkSlider = setInterval(function () {
    const $slider = $('.related-products ul');
    if ($slider.find('li').length > 4) {
      if (!$slider.hasClass('slick-initialized')) {
        $slider.slick({
          slidesToShow: 4,
          arrow:true,
          slidesToScroll: 1,
          prevArrow: '<div class="slick-prev-custom"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-left"> <path d="m15 18-6-6 6-6"></path> </svg></div>',
          nextArrow: '<div class="slick-next-custom"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right"> <path d="m9 18 6-6-6-6"></path> </svg></div>',
           responsive: [
            {
              breakpoint: 768,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                centerMode: true,
                centerPadding: '20px'
              }
            }
          ]
        });
      }
      clearInterval(checkSlider);
    }
  }, 300); 

});


function initMobileSlider() {
  if ($(window).width() <= 767) {
    $('.collection .card__media .media').not('.slick-initialized').slick({
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
    });
  } else {
    $('.collection .card__media .media.slick-initialized').slick('unslick');
  }
}

initMobileSlider();

$(window).on('resize', function () {
  initMobileSlider();
});

document.addEventListener('click', function(e){

  // wishlist click par redirect mat karo
  if(e.target.closest('.wishlist-icon-text')){
    return;
  }

  const item = e.target.closest('.collection li.grid__item');

  if(item){

    const productUrl = item.getAttribute('product-href');

    if(productUrl){
      window.location.href = productUrl;
    }

  }

});


window.addEventListener('load', function () {
  if(window.innerWidth < 749){
    setTimeout(() => {
      const slickTrack = document.querySelector(
        '.collection #product-grid li.grid__item .card--media .card__media .media .slick-track'
      );
      if (!slickTrack) return;
      slickTrack.style.transition = 'transform 1.2s ease';
      slickTrack.style.transform = 'translate3d(-80px, 0px, 0px)';
      setTimeout(() => {
        slickTrack.style.transform = 'translate3d(0px, 0px, 0px)';
      }, 1200);
    }, 300);
  }
});
